import { useFormContext } from './FormProvider';
import { Button } from '@/components/ui/button';

interface FormSubmitButtonProps {
  label: string;
  /**
   * - `'create'`: always enabled (empty/invalid required fields are surfaced
   *   inline on click, not by disabling the button).
   * - `'edit'`: enabled only when the form is dirty, disabled-but-visible when
   *   clean. Relies on the adopter seeding `defaultValues` with the saved
   *   baseline and re-baselining after save (e.g. remounting via a form key).
   */
  mode: 'create' | 'edit';
  /**
   * Extra dirtiness the form's own `isDirty` can't see — child collections
   * edited outside the TanStack form (e.g. billing rows). ORed into the
   * `mode="edit"` dirty gate so those changes still enable Save.
   */
  extraDirty?: boolean;
  disabled?: boolean;
}

export function FormSubmitButton({
  label,
  mode,
  extraDirty = false,
  disabled: externalDisabled = false,
}: FormSubmitButtonProps) {
  const { form, isPending } = useFormContext();

  return (
    <form.Subscribe
      selector={(state) => ({
        isSubmitting: state.isSubmitting,
        isDirty: state.isDirty,
      })}
    >
      {({ isSubmitting, isDirty }) => {
        const inFlight = externalDisabled || isPending || isSubmitting;
        const isDisabled = inFlight || (mode === 'edit' && !isDirty && !extraDirty);

        return (
          <Button type="submit" disabled={isDisabled}>
            {label}
          </Button>
        );
      }}
    </form.Subscribe>
  );
}
